import React from "react";
import styled, { css } from "styled-components";

const Shell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const Header = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
`;

const TitleGroup = styled.div`
    min-width: 0;
`;

const Title = styled.div`
    font-size: 13px;
    font-weight: 800;
    color: #132238;
`;

const Subtitle = styled.div`
    margin-top: 2px;
    font-size: 11px;
    color: #6d7f96;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const Badge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: 20px;
    padding: 0 8px;
    border-radius: 999px;
    background: ${(props) => (props.$resolved ? "#e5ecf4" : "#e7faf3")};
    color: ${(props) => (props.$resolved ? "#5f7489" : "#0f8f6f")};
    font-size: 10px;
    font-weight: 800;
`;

const CloseButton = styled.button`
    width: 28px;
    height: 28px;
    border: 1px solid #d7e0ea;
    border-radius: 8px;
    background: #ffffff;
    color: #496178;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;

    &:hover {
        background: #eef3f7;
    }
`;

const Meta = styled.div`
    display: grid;
    grid-template-columns: minmax(64px, 92px) minmax(0, 1fr);
    gap: 8px 12px;
    font-size: 12px;
`;

const MetaKey = styled.div`
    font-weight: 700;
    color: #29415d;
`;

const MetaValue = styled.div`
    min-width: 0;
    color: #536981;
    word-break: break-word;
`;

const MessageList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: ${(props) => (props.$compact ? "220px" : "280px")};
    overflow: auto;
`;

const MessageCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 12px;
    border: 1px solid #dfe7f0;
    border-radius: 10px;
    background: #ffffff;
`;

const MessageMeta = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 11px;
    color: #72849a;
`;

const MessageContent = styled.div`
    font-size: 13px;
    line-height: 1.5;
    color: #23374d;
    white-space: pre-wrap;
    word-break: break-word;
`;

const Composer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Textarea = styled.textarea`
    min-height: 76px;
    resize: vertical;
    padding: 10px 12px;
    border: 1px solid #d6e0eb;
    border-radius: 10px;
    background: #ffffff;
    color: #203249;
    font-size: 12px;

    &:focus {
        outline: none;
        border-color: #0f8f6f;
        box-shadow: 0 0 0 3px rgba(15, 143, 111, 0.12);
    }
`;

const ActionRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const ActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 32px;
    padding: 0 12px;
    border-radius: 8px;
    border: 1px solid ${(props) => (props.$primary ? "#0f8f6f" : "#d8e1ec")};
    background: ${(props) => (props.$primary ? "#0f8f6f" : "#ffffff")};
    color: ${(props) => (props.$primary ? "#ffffff" : "#29415d")};
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${(props) => (props.$primary ? "#10765d" : "#eef3f7")};
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const panelCardStyle = css`
    padding: 12px;
    border: 1px solid #dde7f1;
    border-radius: 12px;
    background: #ffffff;
`;

const overlayCardStyle = css`
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
`;

const Container = styled.div`
    ${(props) => (props.$variant === "overlay" ? overlayCardStyle : panelCardStyle)}
`;

function getThreadSubtitle(thread) {
    if (thread?.anchor_type === "document_range") return "문서 범위 리뷰";
    if (thread?.anchor_type === "node") return "파일 리뷰";
    return "원고 리뷰";
}

export default function ReviewThreadDetail({
    thread,
    replyDraft,
    onReplyDraftChange,
    onReply,
    onResolve,
    onReopen,
    onClose,
    replyLoading = false,
    resolveLoading = false,
    reopenLoading = false,
    variant = "panel",
}) {
    if (!thread) {
        return null;
    }

    return (
        <Container $variant={variant}>
            <Shell>
                <Header>
                    <TitleGroup>
                        <Title>{thread.anchor_path || "원고 전체"}</Title>
                        <Subtitle>{getThreadSubtitle(thread)}</Subtitle>
                    </TitleGroup>
                    <HeaderActions>
                        <Badge $resolved={thread.is_resolved}>{thread.is_resolved ? "해결됨" : "열림"}</Badge>
                        {onClose ? (
                            <CloseButton type="button" aria-label="리뷰 상세 닫기" onClick={onClose}>
                                ×
                            </CloseButton>
                        ) : null}
                    </HeaderActions>
                </Header>

                {thread.creation_anchor_range?.start_line ? (
                    <Meta>
                        <MetaKey>위치</MetaKey>
                        <MetaValue>
                            L{thread.creation_anchor_range.start_line}:{thread.creation_anchor_range.start_col}-L
                            {thread.creation_anchor_range.end_line}:{thread.creation_anchor_range.end_col}
                        </MetaValue>
                        {thread.creation_anchor_range?.selected_text ? (
                            <>
                                <MetaKey>선택 텍스트</MetaKey>
                                <MetaValue>{thread.creation_anchor_range.selected_text}</MetaValue>
                            </>
                        ) : null}
                    </Meta>
                ) : null}

                <MessageList $compact={variant === "panel"}>
                    {(thread.messages || []).map((message) => (
                        <MessageCard key={message.id}>
                            <MessageMeta>
                                <div>{message.sender_name || message.sender_email || "Unknown"}</div>
                                <div>{message.created_at}</div>
                            </MessageMeta>
                            <MessageContent>{message.content}</MessageContent>
                        </MessageCard>
                    ))}
                </MessageList>

                <Composer>
                    <Textarea
                        value={replyDraft}
                        onChange={(event) => onReplyDraftChange?.(event.target.value)}
                        placeholder="답글을 입력하세요."
                    />
                    <ActionRow>
                        <ActionButton
                            type="button"
                            $primary
                            onClick={onReply}
                            disabled={!replyDraft.trim() || replyLoading || thread.is_resolved}
                        >
                            답글
                        </ActionButton>
                        {thread.is_resolved ? (
                            <ActionButton type="button" onClick={onReopen} disabled={reopenLoading}>
                                다시 열기
                            </ActionButton>
                        ) : (
                            <ActionButton type="button" onClick={onResolve} disabled={resolveLoading}>
                                해결
                            </ActionButton>
                        )}
                    </ActionRow>
                </Composer>
            </Shell>
        </Container>
    );
}
